import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Input } from '../ui/input2';
import { FormField } from "../../types/forms";

interface HeadingSectionProps {
  userId: string;
  templateId: string;
  fields?: FormField[];
  /** Display/editable title – from section.title; edited on right (preview) when isEditable */
  sectionTitle?: string | null;
  /** In builder preview: allow editing title from right side */
  isEditable?: boolean;
  /** When user edits heading in preview, parent updates template */
  onTitleChange?: (value: string) => void;
  /** First field id for API (same as text block title update) */
  firstFieldId?: string | number;
  /** Save title via API – called when user clicks tick */
  onSaveTitle?: (fieldId: string | number, title: string) => void | Promise<void>;
}

export default function HeadingSection({
  userId,
  templateId,
  fields,
  sectionTitle,
  isEditable = false,
  onTitleChange,
  firstFieldId,
  onSaveTitle,
}: HeadingSectionProps) {
  const storageKey = `${userId}_${templateId}_heading`;
  const [localHeading, setLocalHeading] = useLocalStorage<string>(storageKey, "");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const fromField = fields?.[0];
  const fieldValue = fromField
    ? (typeof fromField.options === "string" ? fromField.options : fromField.options?.content ?? fromField.label ?? "")
    : "";
  const displayValue = sectionTitle?.trim() || fieldValue || localHeading;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (isEditable && onTitleChange) {
      onTitleChange(val);
    } else if (!fields?.length) {
      setLocalHeading(val);
    }
  };

  const handleFocus = () => setIsEditingTitle(true);
  const handleBlur = () => setTimeout(() => setIsEditingTitle(false), 150);

  const handleSave = async () => {
    if (!firstFieldId || !onSaveTitle) return;
    setIsSaving(true);
    try {
      await Promise.resolve(onSaveTitle(firstFieldId, displayValue));
      setIsEditingTitle(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => setIsEditingTitle(false);

  const showSaveClose = isEditable && firstFieldId && onSaveTitle && isEditingTitle;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Enter section heading..."
          readOnly={!isEditable && !!fields?.length}
          className="text-lg font-semibold text-gray-800 border-0 border-b border-gray-200 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-[#885ABB] flex-1"
        />
        {showSaveClose && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50"
              title="Save heading"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
